import { useState, useEffect, useCallback } from 'react';
import {
  useApi,
  identityApiRef,
  configApiRef,
} from '@backstage/core-plugin-api';

export interface ProductClick {
  product: string;
  click_count: string;
}

export const useMarketplaceData = () => {
  const identityApi = useApi(identityApiRef);
  const configApi = useApi(configApiRef);
  const baseUrl = configApi.getString('backend.baseUrl');

  const [productClicks, setProductClicks] = useState<ProductClick[]>([]);
  const [usageError, setUsageError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const getUsageCount = (productKey: string): string | number => {
    if (isLoading) return '-';
    const productClick = productClicks.find(pc => pc.product === productKey);
    return productClick ? parseInt(productClick.click_count, 10) : 0;
  };

  const fetchUsageCounts = useCallback(async () => {
    try {
      setIsLoading(true);
      const credentials = await identityApi.getCredentials();

      if (!credentials.token) throw new Error('No authentication token available');

      const response = await fetch(
        `${baseUrl}/api/custom-api/user-metrics/product-clicks`,
        {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${credentials.token}`,
            'Content-Type': 'application/json',
          },
        },
      );

      if (!response.ok) throw new Error('Failed to load usage counts. Usage stats may be unavailable.');

      const data = await response.json();
      setProductClicks(data.rows || []);
    } catch (err: any) {
      setProductClicks([]);
      setUsageError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, [identityApi, baseUrl]);

  const clearUsageError = useCallback(() => setUsageError(null), []);

  useEffect(() => {
    fetchUsageCounts();
  }, [fetchUsageCounts]);

  return {
    productClicks,
    usageError,
    isLoading,
    getUsageCount,
    fetchUsageCounts,
    clearUsageError,
  };
};
